import React, { useMemo } from 'react';
import type { ScheduleRuleGet } from 'c-sdk';
import { getTimeRanges, ScheduleDayToJSDayNumber } from 'c-main/Components/Schedule/lib';
import { useDateUtils } from 'c-hooks';
import { Stack, Typography } from '@mui/material';

import { useCommonTranslation } from 'c-translation';

type Props = {
    rule: ScheduleRuleGet;
    /** UL-8164 -- the rule's dates fall outside the campaign's (e.g. the campaign was shortened). */
    outsideCampaign?: boolean;
};

const ScheduleRuleSummary: React.FC<Props> = ({ rule, outsideCampaign = false }) => {
    const { formatDateString } = useDateUtils();
    const dates = useMemo(
        () =>
            `${rule?.start_date ? formatDateString({ date: new Date(rule?.start_date) }) : ''} ${
                rule?.start_date ? ' - ' : ''
            }${
                rule?.end_date
                    ? formatDateString({
                          date: new Date(rule?.end_date),
                      })
                    : ''
            }`,
        [formatDateString, rule?.start_date, rule?.end_date],
    );
    const timePairs = useMemo(
        () => getTimeRanges(rule?.hours_of_day ?? []).join(', '),
        [rule?.hours_of_day],
    );
    const t = useCommonTranslation();
    const daysOfWeek = useMemo(
        () =>
            (rule?.days_of_week ?? [])
                .map(dayName => t(`WeekdaysShort.${ScheduleDayToJSDayNumber[dayName]}`))
                .join(', '),
        [rule?.days_of_week, t],
    );
    return (
        <Stack gap={0.5}>
            {outsideCampaign && (
                <Typography variant="caption" color="error">
                    {t('Modules.Main.Campaigns.CampaignSchedule.Form.ruleOutsideCampaign')}
                </Typography>
            )}
            <Typography variant="caption">{dates}</Typography>
            <Typography variant="caption">{timePairs}</Typography>
            <Typography variant="caption">
                {rule?.sov ? `${t('Modules.Main.Campaigns.CampaignSchedule.summary.sov')}: ` : ''}{' '}
                {rule?.sov}
            </Typography>
            <Typography variant="caption">
                {rule?.slot_length
                    ? `${t('Modules.Main.Campaigns.CampaignSchedule.summary.slotLength')}: `
                    : ''}
                {rule?.slot_length}
            </Typography>
            <Typography variant="caption">{daysOfWeek}</Typography>
        </Stack>
    );
};

export default ScheduleRuleSummary;
